import type {
  BankAccountMoveToDocumentInput,
  Currency,
  Scalars,
} from "@src/__generated__/graphql";
import { appStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import { convertToWorkspaceCurrency } from "@src/utils/currency";
import {
  currency as currencyFormatter,
  denominate,
  nominate,
} from "@src/utils/formatters";
import type { TToFieldStates } from "@src/utils/forms/ts-utils";
import { required } from "@src/utils/forms/validators";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable } from "mobx";

type FormFields = {
  amount: string;
  exchangeRate: string;
  moveId: Scalars["ID"]["output"];
};

export class SelectedMove extends FormState<TToFieldStates<FormFields>> {
  isEditEnabled = new BooleanState(false);

  constructor(
    fields: FormFields,
    public currency: Currency,
    public referenceCurrency?: Currency,
  ) {
    super({
      amount: new FieldState(fields.amount).validators(required),

      moveId: new FieldState(fields.moveId),
      exchangeRate: new FieldState(
        nominate(
          Number(
            fields.exchangeRate ?? denominate(1, EXCHANGE_RATE_DENOMINATION),
          ),
          EXCHANGE_RATE_DENOMINATION,
        ).toString(),
      ).validators(required),
    });

    makeObservable(this, {
      hasForeignCurrency: computed,
      exchangeRatePrefix: computed,
      nominatedExchangeRate: computed,
      formattedExchangeRate: computed,
      denominatedAmountInDocumentCurrency: computed,
      amountInputValue: computed,
      mutationInput: computed,
      handleAmountChange: action.bound,
      handleEditCancel: action.bound,
      handleEditSave: action.bound,
    });
  }

  get hasForeignCurrency(): boolean {
    const comparisonCurrency =
      this.referenceCurrency ?? appStore.workspaceStore.settings?.currency;
    return this.currency.id !== comparisonCurrency?.id;
  }

  get exchangeRatePrefix() {
    if (!this.referenceCurrency) return null;
    return `${currencyFormatter.formatByCurrency(100, this.referenceCurrency, {
      fractionDigits: 0,
    })} =`;
  }

  get nominatedExchangeRate() {
    return denominate(
      Number(this.$.exchangeRate.value),
      this.currency.denomination,
    );
  }

  get formattedExchangeRate() {
    return currencyFormatter.formatByCurrency(
      this.nominatedExchangeRate,
      this.currency,
    );
  }

  get denominatedAmountInDocumentCurrency(): number {
    return Math.round(
      convertToWorkspaceCurrency(Number(this.$.amount.$), {
        denomination: this.referenceCurrency?.denomination,
        exchange_rate: denominate(
          Number(this.$.exchangeRate.value),
          EXCHANGE_RATE_DENOMINATION,
        ),
      }),
    );
  }

  get amountInputValue(): string {
    if (this.$.amount.value === "") return "";
    return nominate(
      Number(this.$.amount.value),
      this.currency.denomination,
    ).toString();
  }

  get mutationInput(): BankAccountMoveToDocumentInput {
    return {
      bank_account_move_id: this.$.moveId.$,
      amount: Number(this.$.amount.$),
      amount_in_move_currency: this.denominatedAmountInDocumentCurrency,
      exchange_rate: denominate(
        Number(this.$.exchangeRate.$),
        EXCHANGE_RATE_DENOMINATION,
      ),
    };
  }

  handleAmountChange(newValue: string) {
    this.$.amount.onChange(
      denominate(Number(newValue), this.currency.denomination).toString(),
    );
  }

  handleEditCancel() {
    this.isEditEnabled.off();
    this.reset();
  }

  async handleEditSave() {
    const { hasError } = await this.validate();
    if (hasError) return;
    this.$.amount = new FieldState(this.$.amount.$);
    this.$.exchangeRate = new FieldState(this.$.exchangeRate.$);
    this.isEditEnabled.off();
  }
}
